import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Select, DatePicker,
  message, Popconfirm, Descriptions, Divider, Card, AutoComplete, Tooltip, Progress,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EditOutlined, EyeOutlined, CheckOutlined, CloseOutlined,
  PauseOutlined, ToolOutlined, PauseCircleOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useModuleApi } from '../ortak/moduleApi';
import { fmtAmount, fmtDate } from '../finans/shared/format';
import { enhanceColumns, toplamGenislik } from '../ortak/tableHelpers';
import { terminIpucu } from '../../utils/termin';
import { inputNumberMoneyProps } from '../../utils/currency';
import { cihazKonumuAl, konumKontrol } from '../../utils/cihazKonumu';

const DURUM_COLOR = {
  'İzin Bekliyor': 'volcano', 'İzin Reddedildi': 'red', 'Beklemeye Alındı': 'default',
  'Beklemede': 'gold',
  'Talep': 'gold', 'Planlamada': 'blue', 'Kurulum Bekliyor': 'orange', 'Tamamlandı': 'green',
};

// '12,13,20-25' -> QR adedi (canlı önizleme; backend parse_qr_araligi ile birebir)
const isDec = (x) => /^\d+$/.test(x);
const parseQrCount = (s) => {
  const set = new Set();
  String(s || '').replace(/;/g, ',').split(',').forEach((p) => {
    p = p.trim(); if (!p) return;
    if (p.includes('-')) {
      const seg = p.split('-');
      if (seg.length === 2 && isDec(seg[0].trim()) && isDec(seg[1].trim())) {
        let lo = parseInt(seg[0], 10), hi = parseInt(seg[1], 10);
        if (lo > hi) { const t = lo; lo = hi; hi = t; }
        if (hi - lo <= 2000) for (let i = lo; i <= hi; i += 1) set.add(i);
      }
    } else if (isDec(p)) { set.add(parseInt(p, 10)); }
  });
  return set.size;
};

// Sıfır tarihleri ('0000-00-00') ve 1900 öncesini eleyen güvenli dayjs.
// Backend artık bunları None'a çeviriyor (crud_engine._convert) ama eski
// backend'e karşı da doğru çalışsın diye ikinci savunma katmanı.
const SIFIR_TARIH = new Set(['0000-00-00', '0000-00-00 00:00:00', '1900-01-01']);
const safeDayjs = (v) => {
  if (!v || SIFIR_TARIH.has(String(v).trim())) return null;
  const d = dayjs(v);
  return d.isValid() && d.year() > 1900 ? d : null;
};

// Tarih kolonlari icin: sifir/gecersiz tarihlerde '-' goster, siralamada en sona at.
const tarihMetni = (v) => {
  const d = safeDayjs(v);
  return d ? d.format('DD.MM.YYYY') : '-';
};
const zamanDegeri = (v) => {
  const d = safeDayjs(v);
  return d ? d.valueOf() : 0;
};

const userOpts = (lk) => ((lk && lk.users) || []).map((u) => ({ value: u.id, label: u.ad_soyad }));
// Saha/planlama sorumlusu seçimleri yalnızca İNŞAAT EKİBİNİ göstermeli.
// lookups.insaat_ekibi eski bir backend'e karşı boş gelirse tüm personele düşer.
const insaatOpts = (lk) => {
  const ekip = (lk && lk.insaat_ekibi) || [];
  if (!ekip.length) return userOpts(lk);
  return ekip.map((u) => ({
    value: u.id,
    label: u.pozisyon ? `${u.ad_soyad} — ${u.pozisyon}` : u.ad_soyad,
  }));
};
const tipOpts = (lk) => ((lk && lk.kurulum_tip) || []);
const tipLabel = (lk, v) => (tipOpts(lk).find((o) => o.value === v) || {}).label || v;
const userLabel = (lk, id) =>
  id == null || id === 0 ? '-' : ((((lk && lk.users) || []).find((u) => u.id === id) || {}).ad_soyad || id);

const RED_SEBEPLERI = [
  'Esnaf/Vatandaş Şikayeti (Belediye)',
  'Belediye Onay Alınamadı (Belediye)',
  'Polis Sebebi ile (Belediye)',
  'Esnaf/Vatandaş Şikayeti (Özel Mülk)',
  'Site Yönetimi (Özel Mülk)',
  'Yerin Kamu / Belediye Alanı Olması (Özel Mülk)',
];

const TERMIN_GUN = 7;
const TERMIN_HAK = 1;
const terminInfo = (value, completed = false) => {
  if (completed) return { color: 'green', text: 'Tamamlandı', percent: 100 };
  const start = safeDayjs(value) ? safeDayjs(value).startOf('day') : null;
  if (!start || !start.isValid()) return { color: 'default', text: '-', percent: 0 };
  const elapsed = Math.max(0, dayjs().startOf('day').diff(start, 'day'));
  const remaining = TERMIN_GUN - elapsed;
  const delay = elapsed - (TERMIN_GUN + TERMIN_HAK);
  const percent = Math.min(100, Math.round((elapsed / (TERMIN_GUN + TERMIN_HAK)) * 100));
  if (delay > 0) return { color: 'red', text: `${delay} gün gecikme`, percent };
  if (elapsed >= TERMIN_GUN) return { color: 'orange', text: 'Son gün (ek hak)', percent };
  if (remaining <= 2) return { color: 'gold', text: `${remaining} gün kaldı`, percent };
  if (remaining <= 4) return { color: 'lime', text: `${remaining} gün kaldı`, percent };
  return { color: 'green', text: `${remaining} gün kaldı`, percent };
};
// Termin ipucu ortak yardımcıdan gelir (src/utils/termin.jsx) — Süreç Takibi
// ekranıyla aynı gösterim: kuru "7+1 gün" yerine işin ne zaman atandığı.
const TerminCell = ({ value, completed, ekTarihler = [] }) => {
  const info = terminInfo(value, completed);
  return (
    <Tooltip title={terminIpucu([['Görev alındı', value], ...ekTarihler], TERMIN_GUN, TERMIN_HAK)}>
      {/* Progress koşullu olduğu için bazı satırlar tek, bazıları iki satır
          oluyordu ve tablo kayık görünüyordu. Yükseklik sabitlendi. */}
      <div style={{ minWidth: 120, minHeight: 34 }}>
        <Tag color={info.color === 'default' ? undefined : info.color}>{info.text}</Tag>
        {info.text !== '-' && info.text !== 'Tamamlandı' && (
          <Progress percent={info.percent} showInfo={false} size="small" strokeColor={info.color} />
        )}
      </div>
    </Tooltip>
  );
};

// --------------------------------------------------------------------------- İnşaat Takip
export function InsaatTakipTab({ api, lookups }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [planRow, setPlanRow] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [kurulumRow, setKurulumRow] = useState(null);
  const [kurulumQr, setKurulumQr] = useState('');
  const [kurulumAciklama, setKurulumAciklama] = useState('');
  const [kurulumSaving, setKurulumSaving] = useState(false);
  // 07.10 konum doğrulaması: kurulum ancak lokasyona 100 m içinden tamamlanır.
  const [kurulumKonum, setKurulumKonum] = useState(null);
  const [kurulumKonumDurum, setKurulumKonumDurum] = useState({ durum: 'idle', mesaj: '' });
  const [izinRejectRow, setIzinRejectRow] = useState(null);
  const [izinSebep, setIzinSebep] = useState('');
  const [izinAciklama, setIzinAciklama] = useState('');
  const [izinRedSebep, setIzinRedSebep] = useState(null);
  const [izinBekletRow, setIzinBekletRow] = useState(null);
  // İnşaat sürecini (izin aşamasından bağımsız) beklemeye alma
  const [surecBekletRow, setSurecBekletRow] = useState(null);
  const [bekletSebep, setBekletSebep] = useState(null);
  const [bekletAciklama, setBekletAciklama] = useState('');
  // 20.08 madde 6: beklemeyi çözmekle görevlendirilecek TEK kişi (opsiyonel)
  const [bekletSorumlu, setBekletSorumlu] = useState(null);
  const [bekleSebepleri, setBekleSebepleri] = useState([]);
  const surecApi = useModuleApi('/surec');
  const [form] = Form.useForm();
  const [planForm] = Form.useForm();
  const [locOpts, setLocOpts] = useState([]);
  const [locText, setLocText] = useState('');
  const locTimer = useRef(null);

  // Lokasyon metin araması; seçimde İstanbul/Anadolu'ya göre planlama sorumlusu önerilir
  const searchLoc = (q) => {
    setLocText(q);
    if (locTimer.current) clearTimeout(locTimer.current);
    locTimer.current = setTimeout(async () => {
      if (!q || q.length < 2) { setLocOpts([]); return; }
      try {
        const rows = await api.get('/search-lokasyon', { q });
        setLocOpts(rows.map((r) => ({
          value: String(r.MaptriksID),
          label: `${r.lokasyon_ad || ''} (${r.MaptriksID})${r.il_ad ? ` — ${r.il_ad}` : ''}`,
          row: r,
        })));
      } catch { /* sessiz */ }
    }, 300);
  };
  const pickLoc = (val, opt) => {
    const r = opt.row || {};
    setLocText(`${r.lokasyon_ad || ''} (${r.MaptriksID})`);
    const planlama = (lookups && lookups.planlama_sorumlulari) || [];
    const bolge = Number(r.ProvinceID) === 34 ? 'istanbul' : 'anadolu';
    const onerilen = planlama.find((p) => p.bolge === bolge);
    form.setFieldsValue({
      lokasyon: r.MaptriksID,
      ...(onerilen ? { planlama_sorumlusu: onerilen.id } : {}),
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get('/insaat-takip')); }
    catch (e) { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (r) => {
    try { setDetail(await api.get(`/insaat-talep/${r.id}`)); } catch { message.error('Detay alınamadı'); }
  };
  const openPlan = (r) => {
    setPlanRow(r);
    planForm.setFieldsValue({
      saha_sorumlusu: r.saha_sorumlusu || undefined,
      operasyon_planı: safeDayjs(r.operasyon_plani),
      operasyon_aciklama: r.operasyon_aciklama,
    });
  };
  const savePlan = async () => {
    const v = await planForm.validateFields();
    try {
      await api.update('insaat_talep', planRow.id, {
        saha_sorumlusu: v.saha_sorumlusu,
        operasyon_planı: v.operasyon_planı ? dayjs(v.operasyon_planı).format('YYYY-MM-DD') : null,
        operasyon_aciklama: v.operasyon_aciklama,
      });
      message.success('Plan kaydedildi'); setPlanRow(null); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
  };
  const createTalep = async () => {
    const v = await form.validateFields();
    try {
      await api.create('insaat_talep', { ...v, durum: 0 });
      message.success('Talep oluşturuldu'); setCreateOpen(false); form.resetFields(); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'Oluşturulamadı'); }
  };
  const izinKarar = async (row, karar, sebep, aciklama) => {
    if (karar === 'red' && !String(sebep || '').trim()) { message.warning('Red sebebi seçin'); return; }
    if ((karar === 'red' || karar === 'beklet') && !String(aciklama || '').trim()) { message.warning('Açıklama girmek zorunludur'); return; }
    try {
      const res = await surecApi.post(`/izin/${row.id}/karar`, { karar, sebep: sebep || null, aciklama: aciklama || null });
      message.success(res.message || (karar === 'onay' ? 'İzin onaylandı' : karar === 'beklet' ? 'Beklemeye alındı' : 'İzin reddedildi'));
      setIzinRejectRow(null); setIzinBekletRow(null); setIzinSebep(''); setIzinAciklama(''); setIzinRedSebep(null); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };
  const surecBeklet = async () => {
    if (!bekletSebep) { message.warning('Bekleme sebebi seçin'); return; }
    if (!bekletAciklama.trim()) { message.warning('Açıklama zorunludur'); return; }
    try {
      await api.post(`/insaat-talep/${surecBekletRow.id}/beklet`,
        { sebep: bekletSebep, aciklama: bekletAciklama.trim(), sorumlu_id: bekletSorumlu || null });
      message.success(bekletSorumlu ? 'Süreç beklemeye alındı; görev atandı' : 'Süreç beklemeye alındı');
      setSurecBekletRow(null); setBekletSebep(null); setBekletAciklama(''); setBekletSorumlu(null); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };
  const surecDevam = async (r) => {
    try {
      await api.post(`/insaat-talep/${r.id}/devam`, {});
      message.success('Süreç devam ediyor'); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };
  const kurulumAc = (r) => {
    setKurulumRow(r); setKurulumQr(''); setKurulumAciklama('');
    setKurulumKonum(null); setKurulumKonumDurum({ durum: 'idle', mesaj: '' });
  };
  const kurulumKonumAl = async () => {
    setKurulumKonumDurum({ durum: 'loading', mesaj: '' });
    let konum;
    try {
      konum = await cihazKonumuAl();
    } catch (e) {
      setKurulumKonumDurum({ durum: 'error', mesaj: e.message });
      return;
    }
    setKurulumKonum(konum);
    const kontrol = await konumKontrol(konum, { lokasyon: kurulumRow?.lokasyon });
    setKurulumKonumDurum(kontrol
      ? { durum: kontrol.gecerli ? 'ok' : 'error', mesaj: kontrol.mesaj }
      : { durum: 'ok', mesaj: `Konum alındı (±${konum.konum_dogruluk} m)` });
  };
  const kurulumTamamla = async () => {
    if (!kurulumQr.trim()) { message.warning('Kurulan QR/Platform girin (ör. 12,13,20-25)'); return; }
    if (!kurulumKonum) { message.warning("Önce 'Konumu Al' ile konumunuzu ekleyin"); return; }
    setKurulumSaving(true);
    try {
      const res = await api.post(`/insaat-talep/${kurulumRow.id}/kurulum-tamamla`,
        { qr_araligi: kurulumQr, aciklama: kurulumAciklama, konum: kurulumKonum });
      message.success(res.message || 'Kurulum tamamlandı');
      setKurulumRow(null); setKurulumQr(''); setKurulumAciklama(''); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'Kurulum tamamlanamadı'); }
    finally { setKurulumSaving(false); }
  };

  // Liste "Tarih" ve "Sorumlu" ile baslar (Süreç Takibi ekraniyla ayni duzen);
  // varsayilan siralama gorev alinma tarihine gore azalan. Onceki duzende hic
  // tarih kolonu yoktu ve sira yalnizca backend'in ORDER BY it.id DESC'ine
  // bagliydi -- kullanici baska bir kolona tiklayinca geri donulemiyordu.
  const columns = [
    {
      title: 'Görev Alınma',
      dataIndex: 'eklenmeTarihi',
      key: 'gorevAlinma',
      width: 130,
      render: (v) => tarihMetni(v),
      defaultSortOrder: 'descend',
      sorter: (a, b) => zamanDegeri(a.eklenmeTarihi) - zamanDegeri(b.eklenmeTarihi),
    },
    {
      title: 'Plan Tarihi',
      dataIndex: 'operasyon_plani',
      key: 'planTarihi',
      width: 130,
      render: (v) => tarihMetni(v),
      sorter: (a, b) => zamanDegeri(a.operasyon_plani) - zamanDegeri(b.operasyon_plani),
    },
    {
      title: 'Aktif Sorumlu',
      dataIndex: 'aktif_sorumlu',
      width: 150,
      ellipsis: true,
      // Planlama/Saha ayrı kolon değil: iki satırlık hücre satır yüksekliğini
      // bozuyordu. Detay ipucunda ve detay modalinde duruyor.
      render: (v, r) => (
        <Tooltip title={`Planlama: ${r.planlama_ad || '-'}\nSaha: ${r.saha_ad || '-'}`}>
          <span>{v || '-'}</span>
        </Tooltip>
      ),
      sorter: (a, b) => (a.aktif_sorumlu || '').localeCompare(b.aktif_sorumlu || '', 'tr'),
    },
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    { title: 'İl', dataIndex: 'il', width: 90 },
    { title: 'Tip', dataIndex: 'tip', width: 110, render: (v) => tipLabel(lookups, v) },
    { title: 'Kabin', dataIndex: 'kabin', width: 70 },
    {
      title: 'Durum', dataIndex: 'durum_metin', width: 140,
      render: (v, r) => (
        <Tooltip title={r.beklemede
          ? `${r.bekleme_sebebi || ''}${r.bekleme_aciklama ? ` — ${r.bekleme_aciklama}` : ''}${r.bekleme_sorumlu_ad ? ` · Görevli: ${r.bekleme_sorumlu_ad}` : ''}`
          : (r.kurulum_tarihi ? `Kurulum: ${tarihMetni(r.kurulum_tarihi)}` : null)}>
          <Tag color={DURUM_COLOR[v]}>{v}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Termin', key: 'termin', width: 150,
      render: (_, r) => (
        <TerminCell
          value={r.eklenmeTarihi}
          completed={r.durum_metin === 'Tamamlandı'}
          ekTarihler={[
            ['Operasyon planı', r.operasyon_plani],
            ['İzin kararı', r.izin_onay_tarihi],
          ]}
        />
      ),
    },
    {
      // İzin bekleyen satırlarda 6 buton 300px'e sığmayıp alt satıra kayıyor,
      // o satırlar 2-3 kat uzuyor ve tablo "kayık" görünüyordu. Butonlar artık
      // ikon + ipucu ile tek satırda kalır (wrap yok).
      title: 'İşlem', key: 'a', fixed: 'right', width: 210,
      render: (_, r) => (
        <Space size={4} wrap={false}>
          <Tooltip title="Detay">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)} />
          </Tooltip>
          {(r.izin_durum === 'beklemede' || r.izin_durum === 'ertelendi') && (
            <>
              <Popconfirm title={`${r.izin_tipi === 'belediye' ? 'Belediye' : 'Yer Sahibi'} iznini onaylıyor musunuz?`}
                okText="Onayla" cancelText="Vazgeç" onConfirm={() => izinKarar(r, 'onay')}>
                <Tooltip title="İzni onayla">
                  <Button size="small" type="primary" icon={<CheckOutlined />} />
                </Tooltip>
              </Popconfirm>
              <Tooltip title="İzni reddet">
                <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => { setIzinRejectRow(r); setIzinSebep(''); setIzinRedSebep(null); setIzinAciklama(''); }} />
              </Tooltip>
              <Tooltip title="Beklemeye al">
                <Button size="small" icon={<PauseOutlined />}
                  onClick={() => { setIzinBekletRow(r); setIzinAciklama(''); }} />
              </Tooltip>
            </>
          )}
          <Tooltip title={r.izin_durum === 'beklemede' ? 'İzin onayı bekleniyor'
            : r.izin_durum === 'ertelendi' ? 'Beklemeye alındı — izin kararı verilmeli'
            : (r.izin_durum === 'red' ? 'İzin reddedildi' : 'Planla')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openPlan(r)}
              disabled={['beklemede', 'ertelendi', 'red'].includes(r.izin_durum)} />
          </Tooltip>
          {r.durum !== 3 && (r.beklemede ? (
            <Tooltip title="Beklemeden devam et">
              <Button size="small" icon={<PlayCircleOutlined />} onClick={() => surecDevam(r)} />
            </Tooltip>
          ) : (
            <Tooltip title="Süreci beklemeye al">
              <Button size="small" icon={<PauseCircleOutlined />}
                onClick={() => {
                  setSurecBekletRow(r); setBekletSebep(null); setBekletAciklama(''); setBekletSorumlu(null);
                  if (!bekleSebepleri.length) {
                    api.get('/bekleme-sebepleri')
                      .then((rows) => setBekleSebepleri(
                        (rows || []).map((x) => ({ value: x.deger, label: x.etiket }))))
                      .catch(() => setBekleSebepleri([]));
                  }
                }} />
            </Tooltip>
          ))}
          {r.durum === 2 && (
            <Tooltip title="Kurulumu tamamla">
              <Button size="small" type="primary" icon={<ToolOutlined />}
                onClick={() => kurulumAc(r)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Yeni Talep</Button>
        <span style={{ color: '#888' }}>{data.length} kayıt</span>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={enhanceColumns(columns)}
        scroll={{ x: toplamGenislik(enhanceColumns(columns)), y: 480 }} pagination={{ pageSize: 50 }} />

      <Modal title={`Lokasyon Detayı`} open={!!detail} footer={null} width={760} onCancel={() => setDetail(null)} destroyOnClose>
        {detail && (
          <>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Lokasyon">{detail.lokasyon_ad || detail.lokasyon}</Descriptions.Item>
              <Descriptions.Item label="Tip">{tipLabel(lookups, detail.tip)}</Descriptions.Item>
              <Descriptions.Item label="Kabin">{detail.kabin}</Descriptions.Item>
              <Descriptions.Item label="Planlama Sorumlusu">{userLabel(lookups, detail.planlama_sorumlusu)}</Descriptions.Item>
              <Descriptions.Item label="Saha Sorumlusu">{userLabel(lookups, detail.saha_sorumlusu)}</Descriptions.Item>
              <Descriptions.Item label="Kurulum Tarihi">{tarihMetni(detail.kurulum_tarihi)}</Descriptions.Item>
              <Descriptions.Item label="Açıklama">{detail.aciklama}</Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>

      <Modal title="Saha Planı" open={!!planRow} onOk={savePlan} onCancel={() => setPlanRow(null)} okText="Kaydet" cancelText="Vazgeç" destroyOnClose>
        <Form form={planForm} layout="vertical">
          <Form.Item name="saha_sorumlusu" label="Saha Sorumlusu" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={insaatOpts(lookups)} />
          </Form.Item>
          <Form.Item name="operasyon_planı" label="Operasyon Planı (Tarih)"><DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" /></Form.Item>
          <Form.Item name="operasyon_aciklama" label="Açıklama"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Yeni İnşaat Talebi" open={createOpen} onOk={createTalep}
        onCancel={() => { setCreateOpen(false); setLocText(''); setLocOpts([]); }}
        okText="Oluştur" cancelText="Vazgeç" destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item label="Lokasyon" required>
            <AutoComplete value={locText} options={locOpts} onSearch={searchLoc} onSelect={pickLoc}
              placeholder="Lokasyon adı ya da MaptriksID yazın..." allowClear
              onClear={() => form.setFieldsValue({ lokasyon: undefined })} />
          </Form.Item>
          <Form.Item name="lokasyon" hidden rules={[{ required: true, message: 'Lokasyon seçin' }]}>
            <InputNumber />
          </Form.Item>
          <Form.Item name="izin_tipi" label="İzin Türü (Mülk)" initialValue="yer_sahibi"
            rules={[{ required: true, message: 'İzin türü seçin' }]}
            tooltip="Planlamadan önce izin adımı: Özel mülk → Yer Sahibi Onayı, Belediye → Belediye Onayı">
            <Select options={[
              { value: 'yer_sahibi', label: 'Özel Mülk — Yer Sahibi Onayı' },
              { value: 'belediye', label: 'Belediye / Kamu — Belediye Onayı' },
            ]} />
          </Form.Item>
          <Form.Item name="tip" label="Tip" rules={[{ required: true }]}><Select options={tipOpts(lookups)} /></Form.Item>
          <Form.Item name="kabin" label="Kabin Sayısı"><InputNumber style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="model" label="Platform Tipi">
            <Select allowClear placeholder="Platform tipi seçin"
              options={[
                { value: 18, label: 'İBB' },
                { value: 11, label: 'Yeni Mah.' },
                { value: 4, label: 'Nilüfer' },
                { value: 6, label: 'Genel Kabin' },
              ]} />
          </Form.Item>
          <Form.Item name="planlama_sorumlusu" label="Planlama Sorumlusu (lokasyona göre önerilir)">
            <Select options={((lookups && lookups.planlama_sorumlulari) || []).map((p) => ({
              value: p.id, label: `${p.ad_soyad} (${p.bolge === 'istanbul' ? 'İstanbul' : 'Anadolu'})`,
            }))} />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Kurulum Tamamla" open={!!kurulumRow} onOk={kurulumTamamla}
        onCancel={() => setKurulumRow(null)} okText="Kurulumu Bitir" cancelText="Vazgeç"
        confirmLoading={kurulumSaving} destroyOnClose>
        {kurulumRow && <div style={{ marginBottom: 8, color: '#888' }}>{kurulumRow.lokasyon_ad || kurulumRow.lokasyon}</div>}
        <div style={{ marginBottom: 6 }}>Kurulan QR / Platform</div>
        <Input.TextArea rows={2} placeholder="Örn: 12,13,14  veya  20-25  (aradakiler dahil)"
          value={kurulumQr} onChange={(e) => setKurulumQr(e.target.value)} />
        <div style={{ margin: '6px 0', color: '#1677ff' }}>{parseQrCount(kurulumQr)} platform seçildi</div>
        <Input.TextArea rows={2} placeholder="Açıklama (opsiyonel)"
          value={kurulumAciklama} onChange={(e) => setKurulumAciklama(e.target.value)} />
        <div style={{ marginTop: 10, marginBottom: 6 }}>Konum <span style={{ color: 'red' }}>*</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button onClick={kurulumKonumAl} loading={kurulumKonumDurum.durum === 'loading'}>Konumu Al</Button>
          <span style={{ fontSize: 12, color: kurulumKonumDurum.durum === 'error' ? '#ff4d4f' : '#52c41a' }}>
            {kurulumKonumDurum.mesaj || <span style={{ color: '#888' }}>Zorunlu — kurulum, lokasyona en fazla 100 m uzaktan tamamlanabilir.</span>}
          </span>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#888' }}>
          Girilen platformlar bu lokasyona atanır, süreç "Tamamlandı" olur ve enerji süreci başlar.
        </div>
      </Modal>

      <Modal title="İzni Reddet" open={!!izinRejectRow} okText="Reddet" cancelText="Vazgeç"
        okButtonProps={{ danger: true }}
        onOk={() => izinKarar(izinRejectRow, 'red', izinRedSebep || '', izinAciklama)}
        onCancel={() => setIzinRejectRow(null)} destroyOnClose>
        {izinRejectRow && <div style={{ marginBottom: 8, color: '#888' }}>{izinRejectRow.lokasyon_ad || izinRejectRow.lokasyon}</div>}
        <div style={{ marginBottom: 6 }}>Red Sebebi (zorunlu)</div>
        <Select style={{ width: '100%', marginBottom: 10 }} value={izinRedSebep} placeholder="Sebep seçin"
          onChange={setIzinRedSebep}
          options={RED_SEBEPLERI.map((s) => ({ value: s, label: s }))} />
        <div style={{ marginBottom: 6 }}>Açıklama (zorunlu)</div>
        <Input.TextArea rows={3} placeholder="Açıklama girmek zorunludur"
          value={izinAciklama} onChange={(e) => setIzinAciklama(e.target.value)} />
      </Modal>

      <Modal title="Süreci Beklemeye Al" open={!!surecBekletRow} okText="Beklemeye Al"
        cancelText="Vazgeç" onOk={surecBeklet} onCancel={() => setSurecBekletRow(null)} destroyOnClose>
        {surecBekletRow && (
          <div style={{ marginBottom: 8, color: '#888' }}>
            {surecBekletRow.lokasyon_ad || surecBekletRow.lokasyon}
          </div>
        )}
        <div style={{ fontSize: 12, marginBottom: 4 }}>Bekleme Sebebi *</div>
        <Select style={{ width: '100%', marginBottom: 8 }} placeholder="Sebep seçin"
          options={bekleSebepleri} value={bekletSebep} onChange={setBekletSebep} />
        <div style={{ fontSize: 12, marginBottom: 4 }}>Açıklama *</div>
        <Input.TextArea rows={3} value={bekletAciklama}
          onChange={(e) => setBekletAciklama(e.target.value)}
          placeholder="Neden beklemeye alınıyor?" />
        <div style={{ fontSize: 12, margin: '8px 0 4px' }}>
          Görev atanacak kişi <span style={{ color: '#8c8c8c' }}>(opsiyonel)</span>
        </div>
        <Select style={{ width: '100%' }} showSearch allowClear optionFilterProp="label"
          placeholder="Beklemeyi çözmekle görevlendirilecek tek kişi"
          options={userOpts(lookups)} value={bekletSorumlu} onChange={(v) => setBekletSorumlu(v || null)} />
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
          Seçilen kişiye "Bekleme takibi" görevi açılır ve bildirim gider; süreç devam ettirilince kapanır.
        </div>
      </Modal>

      <Modal title="Beklemeye Al" open={!!izinBekletRow} okText="Beklemeye Al" cancelText="Vazgeç"
        onOk={() => izinKarar(izinBekletRow, 'beklet', null, izinAciklama)}
        onCancel={() => setIzinBekletRow(null)} destroyOnClose>
        {izinBekletRow && <div style={{ marginBottom: 8, color: '#888' }}>{izinBekletRow.lokasyon_ad || izinBekletRow.lokasyon}</div>}
        <div style={{ marginBottom: 6 }}>Açıklama (zorunlu)</div>
        <Input.TextArea rows={3} placeholder="Beklemeye alma sebebi (zorunlu)"
          value={izinAciklama} onChange={(e) => setIzinAciklama(e.target.value)} />
      </Modal>
    </div>
  );
}

// --------------------------------------------------------------------------- Kurulum Onay
const APPROVER_SLOTS = [['onay1', 3], ['onay2', 11], ['onay3', 36], ['onay4', 4]];
export function KurulumOnayTab({ api, lookups, currentUserId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectRow, setRejectRow] = useState(null);
  const [sebep, setSebep] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.list('kurulum_talep', { limit: 500 })); }
    catch { message.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try { const res = await api.post(`/kurulum-talep/${r.id}/approve`); message.success(res.message || 'Onaylandı'); load(); }
    catch (e) { message.error(e?.response?.data?.detail || 'Onay başarısız'); }
  };
  const doReject = async () => {
    try { await api.post(`/kurulum-talep/${rejectRow.id}/reject`, { iptal_sebebi: sebep }); message.success('Reddedildi'); setRejectRow(null); setSebep(''); load(); }
    catch (e) { message.error(e?.response?.data?.detail || 'Red başarısız'); }
  };

  const onaySum = (r) => (r.onay1 || 0) + (r.onay2 || 0) + (r.onay3 || 0) + (r.onay4 || 0);
  const canApprove = APPROVER_SLOTS.some(([, uid]) => uid === currentUserId);

  const columns = [
    { title: '#', dataIndex: 'id', width: 60 },
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true, render: (v, r) => v || r.lokasyon },
    { title: 'Tip', dataIndex: 'tip', width: 100, render: (v) => tipLabel(lookups, v) },
    { title: 'Toplam', dataIndex: 'toplam', render: (v) => fmtAmount(v) },
    {
      title: 'Onaylar', key: 'on', width: 200,
      render: (_, r) => (
        <Space size={2}>
          {APPROVER_SLOTS.map(([slot]) => (
            <Tag key={slot} color={r[slot] ? 'green' : 'default'}>{slot.replace('onay', 'O')}</Tag>
          ))}
          <b>{onaySum(r)}/4</b>
        </Space>
      ),
    },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 170,
      render: (_, r) => (
        <Space size={4}>
          <Popconfirm title="Onaylıyor musunuz?" okText="Onayla" cancelText="Vazgeç" onConfirm={() => approve(r)} disabled={!canApprove}>
            <Button size="small" type="primary" icon={<CheckOutlined />} disabled={!canApprove}>Onayla</Button>
          </Popconfirm>
          <Button size="small" danger icon={<CloseOutlined />} onClick={() => setRejectRow(r)}>Reddet</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        {!canApprove && <Tag color="orange">Onay yetkiniz yok (görüntüleme)</Tag>}
        <span style={{ color: '#888' }}>{data.length} kayıt</span>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={enhanceColumns(columns)}
        scroll={{ x: toplamGenislik(enhanceColumns(columns)), y: 480 }} pagination={{ pageSize: 50 }} />
      <Modal title="Kurulum Talebi Reddet" open={!!rejectRow} okText="Reddet" cancelText="Vazgeç"
        okButtonProps={{ danger: true }} onOk={doReject} onCancel={() => setRejectRow(null)} destroyOnClose>
        <Input.TextArea rows={3} placeholder="İptal sebebi" value={sebep} onChange={(e) => setSebep(e.target.value)} />
      </Modal>
    </div>
  );
}

// NOT: Eski 'Enerji Takip' ve 'Enerji Süreç' sekmeleri KALDIRILDI.
// İkisi de enerji_takip / kurulum_bilgileri alanlarına serbest DatePicker ile
// yazıyor ve akış motorunu (services/enerji_surec.py) tamamen bypass ediyordu.
// Yerlerini adım/durum/termin kurallarını uygulayan EnerjiSurecPage aldı.

// --------------------------------------------------------------------------- Proje / İhale Masrafları
// İhale süreci giderleri (seyahat, temsil, konaklama...) -> maliyetler (masraf_tipi=10)
// Finans (Nakit Akış) altında sekme olarak kullanılır; proje = kesif_talepleri metin aramasıyla seçilir.
export function ProjeMasrafTab({ api: apiProp }) {
  const ownApi = useModuleApi('/construction');
  const api = apiProp || ownApi;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [projeOpts, setProjeOpts] = useState([]);
  const [projeText, setProjeText] = useState('');
  const projeTimer = useRef(null);

  const searchProje = (q) => {
    setProjeText(q);
    if (projeTimer.current) clearTimeout(projeTimer.current);
    projeTimer.current = setTimeout(async () => {
      if (!q || q.length < 2) { setProjeOpts([]); return; }
      try {
        const rows = await api.get('/search-proje', { q });
        setProjeOpts(rows.map((r) => ({
          value: String(r.id),
          label: `${r.ad}${r.il_ad ? ` — ${r.il_ad}` : ''}${r.banka_ad ? ` (${r.banka_ad})` : ''}`,
          row: r,
        })));
      } catch { /* sessiz */ }
    }, 300);
  };
  const pickProje = (val, opt) => {
    const r = opt.row || {};
    setProjeText(r.ad || val);
    form.setFieldsValue({ kesif_talep_id: r.id });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.list('proje_masraf', { limit: 500 })); }
    catch (e) { message.error(e?.response?.data?.detail || 'Yüklenemedi'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditRow(null);
    form.resetFields();
    setProjeText('');
    form.setFieldsValue({ masraf_tarihi: dayjs() });
    setModalOpen(true);
  };
  const openEdit = (r) => {
    setEditRow(r);
    setProjeText(r.proje_ad || '');
    form.setFieldsValue({
      masraf_adi: r.masraf_adi, tutar: r.tutar, kesif_talep_id: r.kesif_talep_id,
      aciklama: r.aciklama, masraf_tarihi: safeDayjs(r.masraf_tarihi),
    });
    setModalOpen(true);
  };
  const submit = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    const payload = {
      masraf_adi: v.masraf_adi, tutar: v.tutar, kesif_talep_id: v.kesif_talep_id || null,
      aciklama: v.aciklama, masraf_tipi: 10,
      masraf_tarihi: v.masraf_tarihi ? dayjs(v.masraf_tarihi).format('YYYY-MM-DD') : null,
    };
    try {
      if (editRow) { await api.update('proje_masraf', editRow.id, payload); message.success('Güncellendi'); }
      else { await api.create('proje_masraf', payload); message.success('Eklendi'); }
      setModalOpen(false); load();
    } catch (e) { message.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
  };
  const doDelete = async (r) => {
    try { await api.remove('proje_masraf', r.id); message.success('Silindi'); load(); }
    catch (e) { message.error(e?.response?.data?.detail || 'Silinemedi'); }
  };

  const columns = [
    { title: 'Tarih', dataIndex: 'masraf_tarihi', width: 110, render: fmtDate },
    { title: 'Masraf', dataIndex: 'masraf_adi', ellipsis: true },
    { title: 'Tutar', dataIndex: 'tutar', width: 130, render: (v) => fmtAmount(v, 'TRY') },
    { title: 'Proje', dataIndex: 'proje_ad', ellipsis: true, render: (v, r) => v || (r.kesif_talep_id ? `#${r.kesif_talep_id}` : '-') },
    { title: 'Açıklama', dataIndex: 'aciklama', ellipsis: true },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 100,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => doDelete(r)}>
            <Button size="small" danger icon={<CloseOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Masraf</Button>
        <span style={{ color: '#888' }}>
          {data.length} kayıt · Toplam {fmtAmount(data.reduce((s, r) => s + (Number(r.tutar) || 0), 0), 'TRY')}
        </span>
      </Space>
      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={enhanceColumns(columns)}
        scroll={{ x: toplamGenislik(enhanceColumns(columns)), y: 480 }} pagination={{ pageSize: 50 }} />

      <Modal title={editRow ? 'Masraf Düzenle' : 'Yeni Proje/İhale Masrafı'} open={modalOpen}
        onOk={submit} onCancel={() => setModalOpen(false)} okText="Kaydet" cancelText="Vazgeç" destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="masraf_adi" label="Masraf Adı" rules={[{ required: true }]}>
            <Input maxLength={23} placeholder="örn. Seyahat, Konaklama, Temsil" />
          </Form.Item>
          <Space wrap>
            <Form.Item name="tutar" label="Tutar" rules={[{ required: true }]}>
              <InputNumber {...inputNumberMoneyProps} style={{ width: 160 }} step={0.01} min={0} />
            </Form.Item>
            <Form.Item name="masraf_tarihi" label="Tarih" rules={[{ required: true }]}>
              <DatePicker format="DD.MM.YYYY" />
            </Form.Item>
          </Space>
          <Form.Item label="Proje (Keşif Talebi)">
            <AutoComplete value={projeText} options={projeOpts} onSearch={searchProje} onSelect={pickProje}
              placeholder="Proje adı yazın (keşif taleplerinde arar)..." allowClear
              onClear={() => form.setFieldsValue({ kesif_talep_id: undefined })} />
          </Form.Item>
          <Form.Item name="kesif_talep_id" hidden><InputNumber /></Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
